import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  LogOut,
  LayoutDashboard,
  Star,
  Mail,
  Sparkles,
  BookOpen,
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "./ThemeToggle";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Animation variants
  const navbarVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  const menuItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.1, duration: 0.3 },
    }),
  };

  // Define routes where navbar should NOT appear
  const noNavbarRoutes = ["/dashboard", "/projects", "/settings", "/integrations", "/repo", "/project"];
  
  // Check if the current path starts with any of the no-navbar routes
  const shouldHideNavbar = noNavbarRoutes.some(route => location.pathname.startsWith(route));

  if (shouldHideNavbar) {
    return null;
  }
  
  if (window.location.pathname === "/" || window.location.pathname === "/docs" || window.location.pathname === "/about" || window.location.pathname === "/privacy-policy" || window.location.pathname === "/terms-of-use" || window.location.pathname === "/pricing" || window.location.pathname === "/blog") {
    return (
      <motion.nav
        className={`fixed top-0 w-full z-50 border-b border-opacity-40 transition-all duration-300 ${
          scrolled
            ? `${theme === 'dark' 
                ? (window.location.pathname === "/docs" ? "bg-[#030405]" : "bg-[#030405]/80") 
                : "bg-background-light/80"} backdrop-blur-lg shadow-lg ${theme === 'dark' ? 'border-gray-800/30' : 'border-gray-300/30'}`
            : `${theme === 'dark' 
                ? (window.location.pathname === "/docs" ? "bg-[#030405]" : "") 
                : ""} ${theme === 'dark' ? 'bg-[#030405]/40' : 'bg-background-light/40'} backdrop-blur-md`
        }`}
        variants={navbarVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="container flex h-16 items-center justify-between px-4 mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <motion.img
              src="/docgen-logo.png"
              alt="DocGen Logo"
              className="h-10 w-10 transition-transform duration-300"
              whileHover={{ scale: 1.15, rotate: 0, transition: { duration: 0.5 } }}
            />
            <motion.span
              className={`font-semibold text-lg ${theme === 'dark' ? 'text-white group-hover:text-white/80' : 'text-gray-800 group-hover:text-gray-600'} transition duration-300`}
              whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
            >
              DocGen
            </motion.span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {/* About Us Button (Desktop) */}

            {
  window.location.pathname === "/about" ? (
    <></>
  ) : (
    <motion.div
      whileHover={{ 
        scale: 1.05,
        transition: { duration: 0.2, ease: "easeOut" }
      }}
      whileTap={{ 
        scale: 0.95,
        transition: { duration: 0.1 }
      }}
      className="relative"
    >
      <Link to="/about">
        <Button
          variant="ghost"
          className={`relative ${theme === 'dark' ? 'text-white' : 'text-gray-700'} font-medium px-6 py-2.5 rounded-lg overflow-hidden
            ${theme === 'dark' 
              ? 'bg-[#050607]/50 hover:text-white hover:bg-[#050607]/70' 
              : 'bg-gradient-to-r from-gray-200/70 to-gray-100/70 hover:text-gray-900 hover:from-gray-200/90 hover:to-gray-100/90'}
            transition-all duration-300
            group`}
        >
          <span className="relative z-10">About Us</span>
          {/* Decorative elements */}
          <span className={`absolute inset-0 ${theme === 'dark' ? 'bg-white/0 group-hover:bg-white/5' : 'bg-black/0 group-hover:bg-black/5'} transition-all duration-300`} />
          <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 ${theme === 'dark' ? 'bg-white/40' : 'bg-gray-700/40'}
            group-hover:w-[60%] transition-all duration-300 ease-out`} />
        </Button>
      </Link>
    </motion.div>
  )
}

            {/* Pricing Button (Desktop) */}
            <motion.div
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.2, ease: "easeOut" }
              }}
              whileTap={{ 
                scale: 0.95,
                transition: { duration: 0.1 }
              }}
              className="relative"
            >
              <Link to="/pricing">
                <Button
                  variant="ghost"
                  className={`relative ${theme === 'dark' ? 'text-white' : 'text-gray-700'} font-medium px-6 py-2.5 rounded-lg overflow-hidden
                    ${theme === 'dark' 
                      ? 'bg-[#050607]/50 hover:text-white hover:bg-[#050607]/70' 
                      : 'bg-gradient-to-r from-gray-200/70 to-gray-100/70 hover:text-gray-900 hover:from-gray-200/90 hover:to-gray-100/90'}
                    transition-all duration-300
                    group`}
                >
                  <span className="relative z-10">Pricing</span>
                  {/* Decorative elements */}
                  <span className={`absolute inset-0 ${theme === 'dark' ? 'bg-white/0 group-hover:bg-white/5' : 'bg-black/0 group-hover:bg-black/5'} transition-all duration-300`} />
                  <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 ${theme === 'dark' ? 'bg-white/40' : 'bg-gray-700/40'}
                    group-hover:w-[60%] transition-all duration-300 ease-out`} />
                </Button>
              </Link>
            </motion.div>  

            {/* Blog Button (Desktop) */}
            <motion.div
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.2, ease: "easeOut" }
              }}
              whileTap={{ 
                scale: 0.95,
                transition: { duration: 0.1 }
              }}
              className="relative"
            >
              <Link to="/blog">
                <Button
                  variant="ghost"
                  className={`relative ${theme === 'dark' ? 'text-white' : 'text-gray-700'} font-medium px-6 py-2.5 rounded-lg overflow-hidden
                    ${theme === 'dark' 
                      ? 'bg-[#050607]/50 hover:text-white hover:bg-[#050607]/70' 
                      : 'bg-gradient-to-r from-gray-200/70 to-gray-100/70 hover:text-gray-900 hover:from-gray-200/90 hover:to-gray-100/90'}
                    transition-all duration-300
                    group`}
                >
                  <span className="relative z-10">Blog</span>
                  {/* Decorative elements */}
                  <span className={`absolute inset-0 ${theme === 'dark' ? 'bg-white/0 group-hover:bg-white/5' : 'bg-black/0 group-hover:bg-black/5'} transition-all duration-300`} />
                  <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 ${theme === 'dark' ? 'bg-white/40' : 'bg-gray-700/40'}
                    group-hover:w-[60%] transition-all duration-300 ease-out`} />
                </Button>
              </Link>
            </motion.div>

            {/* Theme toggle button */}
            <ThemeToggle />
            
            {/* User Profile or Login Buttons */}
            {user && localStorage.getItem("token") ? (
              <motion.div
                className={`relative flex items-center cursor-pointer gap-2 px-2 py-1 rounded-md ${
                  theme === 'dark' 
                    ? 'bg-[#050607]/60 backdrop-blur-sm border border-gray-800/50 shadow-sm hover:border-gray-700/70' 
                    : 'bg-gray-100/70 backdrop-blur-sm border border-gray-200/50 shadow-sm hover:border-gray-300'
                } transition-all duration-300`}
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 400 }}
                onClick={() => navigate("/dashboard")}
              >
                <div className="relative">
                  <Avatar className={`h-7 w-7 ${theme === 'dark' ? 'ring-1 ring-gray-600' : 'ring-1 ring-gray-300'}`}>
                    <AvatarImage src="/user-avatar.png" alt="User Avatar" />
                    <AvatarFallback className={theme === 'dark' ? 'bg-gray-800 text-xs' : 'bg-gray-200 text-xs'}>
                      {user?.name?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  {user?.email_confirmed && (
                    <div className={`absolute -bottom-0.5 -right-0.5 h-2 w-2 bg-green-500 rounded-full ${theme === 'dark' ? 'border border-slate-800' : 'border border-white'}`} />
                  )}
                </div>
                <div className="flex flex-col justify-center">
                  <h2 className={`text-xs font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-800'} leading-tight truncate max-w-32`}>
                    {user?.name || "User"}
                  </h2>
                  <p className="text-xs text-gray-400 leading-tight truncate max-w-32">
                    {user?.email || "user"}
                  </p>
                </div>
                <span className={`text-xs font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-800'} ml-2`}>Dashboard</span>
              </motion.div>
            ) : (
              <div className="flex items-center gap-3">
                <motion.div
                  whileHover={{ scale: 1.05, transition: { type: "spring", stiffness: 400, damping: 10 } }}
                  whileTap={{ scale: 0.95, transition: { type: "spring", stiffness: 500, damping: 10 } }}
                  className="relative"
                >
                  <Button 
                    className="bg-[#0E9F6E] hover:bg-[#0b8a5f] text-white px-6 py-2 font-medium shadow-sm transition-colors duration-200 rounded-full"
                    onClick={() => window.open("https://cal.com/aniket-docgen/30min", "_blank")}
                  >
                    Book a demo
                  </Button>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.05, transition: { type: "spring", stiffness: 400, damping: 10 } }}
                  whileTap={{ scale: 0.95, transition: { type: "spring", stiffness: 500, damping: 10 } }}
                  className="relative"
                >
                  <Button 
                    variant="outline"
                    className={`${theme === 'dark' 
                      ? 'border-primary text-primary hover:bg-primary/10' 
                      : 'border-primary-light text-primary-light hover:bg-primary-light/10'} 
                      hover:text-primary px-6 py-2 font-medium shadow-sm transition-colors duration-200 rounded-full`}
                    onClick={() => navigate('/auth/login')}
                  >
                    Login
                  </Button>
                </motion.div>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle className="mr-2" />
            <motion.div className="flex items-center" whileTap={{ scale: 0.9 }}>
              <Button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                variant="ghost"
                size="sm"
                className={`${theme === 'dark' 
                  ? 'bg-[#050607]/50 hover:bg-[#050607]/70 text-white' 
                  : 'bg-gray-200/50 hover:bg-gray-300/50 text-gray-700'} rounded-lg p-2`}
              >
                <motion.div animate={{ rotate: isMobileMenuOpen ? 90 : 0 }} transition={{ duration: 0.3, type: "spring", stiffness: 260 }}>
                  {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </motion.div>
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className={`md:hidden border-t ${theme === 'dark' 
                ? 'border-gray-800/30 bg-[#030405]/90' 
                : 'border-gray-300/30 bg-gray-100/90'} backdrop-blur-lg shadow-lg`}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto", transition: { duration: 0.4, ease: "easeInOut" } }}
              exit={{ opacity: 0, height: 0, transition: { duration: 0.3, ease: "easeInOut" } }}
            >
              <div className="py-4 px-6 space-y-1">
                {/* About Us Button (Mobile) */}
                <motion.div custom={1} variants={menuItemVariants} initial="hidden" animate="visible">
                  <Link to="/about" className={`block py-3 flex items-center gap-2 ${theme === 'dark' 
                    ? 'text-white hover:text-white' 
                    : 'text-gray-700 hover:text-gray-900'} transition duration-300`}>
                    <Star className="h-5 w-5" />
                    About Us
                  </Link>
                </motion.div>

                {/* Pricing Button (Mobile) */}
                <motion.div custom={2} variants={menuItemVariants} initial="hidden" animate="visible">
                  <Link to="/pricing" className={`block py-3 flex items-center gap-2 ${theme === 'dark' 
                    ? 'text-white hover:text-white' 
                    : 'text-gray-700 hover:text-gray-900'} transition duration-300`}>
                    <Sparkles className="h-5 w-5" />
                    Pricing
                  </Link>
                </motion.div>

                {/* Blog Button (Mobile) */}
                <motion.div custom={3} variants={menuItemVariants} initial="hidden" animate="visible">
                  <Link to="/blog" className={`block py-3 flex items-center gap-2 ${theme === 'dark' 
                    ? 'text-white hover:text-white' 
                    : 'text-gray-700 hover:text-gray-900'} transition duration-300`}>
                    <BookOpen className="h-5 w-5" />
                    Blog
                  </Link>
                </motion.div>

                {!user || !localStorage.getItem("token") ? (
                  <motion.div custom={4} variants={menuItemVariants} initial="hidden" animate="visible">
                    <div className="grid grid-cols-1 gap-3 mt-4">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className={`w-full rounded-full ${theme === 'dark' 
                          ? 'border-gray-700 hover:bg-gray-700/50 hover:border-gray-600 hover:text-white'
                          : 'border-gray-300 hover:bg-gray-200/50 hover:border-gray-400 hover:text-gray-900'}`}
                        onClick={() => window.open("https://cal.com/aniket-docgen/30min", "_blank")}
                      >
                        Book a Demo
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        className={`w-full rounded-full ${theme === 'dark' 
                          ? 'border-[#0E9F6E] text-[#0E9F6E] hover:bg-[#0E9F6E]/10'
                          : 'border-[#0E9F6E] text-[#0E9F6E] hover:bg-[#0E9F6E]/10'}`}
                        onClick={() => navigate('/auth/login')}
                      >
                        Login
                      </Button>

                      <Button 
                        size="sm"
                        className="w-full rounded-full bg-gradient-to-r from-primary to-blue-500 text-white hover:opacity-90"
                        onClick={() => window.location.href = "mailto:aniket@docgen.dev"}
                      >
                        Contact Us
                      </Button>
                    </div>
                  </motion.div>
                ) : (
                  <>
                    <motion.div custom={4} variants={menuItemVariants} initial="hidden" animate="visible">
                      <div className={`mt-4 border-t ${theme === 'dark' ? 'border-gray-700/50' : 'border-gray-300/50'} pt-4`}>
                        <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>{user?.name || "User"}</p>
                        <p className="text-xs text-gray-400">{user?.email || "user@example.com"}</p>
                      </div>
                    </motion.div>
                    <motion.div custom={5} variants={menuItemVariants} initial="hidden" animate="visible">
                      <Link to="/dashboard" className={`block py-3 flex items-center gap-2 ${theme === 'dark' 
                        ? 'text-white hover:text-white/80' 
                        : 'text-gray-700 hover:text-gray-900'}`}>
                        <LayoutDashboard className="h-5 w-5" />
                        Dashboard
                      </Link>
                    </motion.div>
                    <motion.div custom={6} variants={menuItemVariants} initial="hidden" animate="visible">
                      <button
                        onClick={() => {
                          logout();
                          navigate("/");
                        }}
                        className="block py-3 w-full text-left flex items-center gap-2 text-red-400 hover:text-red-300"
                      >
                        <LogOut className="h-5 w-5" />
                        Logout
                      </button>
                    </motion.div>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    );
  } else {
    return null;
  }
};